import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogBody,
  DialogActions,
  DialogLabel,
  DialogInput,
  DialogTextarea,
} from "../dialog";
import { Button } from "../button";
import { Badge } from "../badge";
import { DateTime } from "luxon";
import { Alert, AlertTitle, AlertDescription, AlertActions } from "../alert";
import { showAlert } from "../../alertService";
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  SparklesIcon,
  Squares2X2Icon,
  LockClosedIcon,
} from "@heroicons/react/20/solid";
import { getClashingEvents, ClashWarning } from "./clashes";
import EspaciosPicker from "../shared/EspaciosPicker";
import ClienteEventoDialog from "../directorio/ClienteEventoDialog";
import { useVenue } from "../VenueProvider";
import { camposEventoDesdeCliente, nombreCliente, contactoCliente } from "../../utils/clientes";

const ACCENT = "#1b4d2e";

const EVENT_TYPE_CONFIG = {
  scouting: { label: "Scouting", badge: "orange" },
  event: { label: "Evento", badge: "emerald" },
  reunion: { label: "Reunión", badge: "blue" },
  bloqueo: { label: "Bloqueo", badge: "red" },
};

const nameRender = {
  undefined: "Nuevo Evento",
  "": "Nuevo Evento",
  scouting: "Nuevo Scouting",
  event: "Nuevo Evento",
  reunion: "Nueva Reunión",
  bloqueo: "Nuevo Bloqueo",
};

/* ── Section wrapper ─────────────────────────────────────────────── */
// Los cuatro tipos como cuadrícula: se ve de un vistazo qué es cada cosa.
// Colores en espejo de los del calendario (calendario/index.js).
const TIPOS_EVENTO = [
  { value: "event", label: "Evento", hint: "Con cliente, cotización y pagos", icon: CalendarDaysIcon, color: ACCENT },
  { value: "scouting", label: "Scouting", hint: "Visita de un prospecto", icon: SparklesIcon, color: "#FF9900" },
  { value: "reunion", label: "Reunión", hint: "Junta o cita en el venue", icon: UserIcon, color: "#3B82F6" },
  { value: "bloqueo", label: "Bloqueo", hint: "Mantenimiento, descanso, cerrado", icon: LockClosedIcon, color: "#DC2626" },
];

const Section = ({ icon: Icon, title, children, className = "" }) => (
  <div className={`flex h-full flex-col rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/50 p-4 ${className}`}>
    <div className="flex items-center gap-2 mb-3">
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
        style={{ backgroundColor: ACCENT + "1a" }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: ACCENT }} />
      </div>
      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
        {title}
      </span>
    </div>
    <div className="flex-1 space-y-3">{children}</div>
  </div>
);

/* ── DateTimeSelect (unchanged logic, lighter visuals) ───────────── */
const DateTimeSelect = ({
  label,
  value,
  onChange,
  spanishMonths,
  getYears,
  getDaysInMonth,
  timeZone,
}) => {
  const dateTime = DateTime.fromISO(value).setZone(timeZone);

  const baseSelectStyles = `
    appearance-none rounded-lg border border-zinc-950/10
    bg-white px-2.5 py-1.5 text-sm text-zinc-950 shadow-sm
    transition-colors focus:border-zinc-950/10 focus:outline-none focus:ring-none
    dark:border-white/10 dark:bg-white/5 dark:text-white
    disabled:cursor-not-allowed disabled:opacity-50 w-full
  `;

  const selectWrapperStyles = `
    relative after:pointer-events-none after:absolute after:right-2.5
    after:top-[50%] after:h-2 after:w-2 after:translate-y-[-50%]
    after:rotate-45 after:border-r-2 after:border-b-2
    after:border-zinc-500 after:content-[''] dark:after:border-zinc-400
  `;

  return (
    <div className="mt-3">
      <DialogLabel>{label}</DialogLabel>
      {/* Fecha y hora en dos grupos que se envuelven: en media columna la
          hora baja a un segundo renglón en vez de aplastar los cinco selects
          en uno (los números se cortaban). Anchos fijos para día, año, hora
          y minuto; el mes toma lo que sobra. */}
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="grid flex-1 min-w-[15rem] grid-cols-[4.25rem_minmax(6.5rem,1fr)_5.25rem] gap-2">
        <div className={selectWrapperStyles}>
          <select
            className={`${baseSelectStyles} pr-6`}
            value={dateTime.day}
            onChange={(e) => {
              const newDT = dateTime.set({ day: parseInt(e.target.value) });
              onChange(newDT.toISO());
            }}
          >
            {Array.from(
              { length: getDaysInMonth(dateTime.year, dateTime.month - 1) },
              (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              )
            )}
          </select>
        </div>
        <div className={selectWrapperStyles}>
          <select
            className={`${baseSelectStyles} pr-6`}
            value={dateTime.month - 1}
            onChange={(e) => {
              const newDT = dateTime.set({ month: parseInt(e.target.value) + 1 });
              onChange(newDT.toISO());
            }}
          >
            {spanishMonths.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>
        </div>
        <div className={selectWrapperStyles}>
          <select
            className={`${baseSelectStyles} pr-6`}
            value={dateTime.year}
            onChange={(e) => {
              const newDT = dateTime.set({ year: parseInt(e.target.value) });
              onChange(newDT.toISO());
            }}
          >
            {getYears().map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        </div>
        <div className="flex items-center gap-1">
        <div className={`w-[4.25rem] ${selectWrapperStyles}`}>
          <select
            className={`${baseSelectStyles} pr-6`}
            value={dateTime.hour}
            onChange={(e) => {
              const newDT = dateTime.set({ hour: parseInt(e.target.value) });
              onChange(newDT.toISO());
            }}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {String(i).padStart(2, "0")}
              </option>
            ))}
          </select>
        </div>
        <span className="text-sm text-zinc-400">:</span>
        <div className={`w-[4.25rem] ${selectWrapperStyles}`}>
          <select
            className={`${baseSelectStyles} pr-6`}
            value={dateTime.minute}
            onChange={(e) => {
              const newDT = dateTime.set({ minute: parseInt(e.target.value) });
              onChange(newDT.toISO());
            }}
          >
            {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
              <option key={m} value={m}>
                {String(m).padStart(2, "0")}
              </option>
            ))}
          </select>
        </div>
        </div>
      </div>
    </div>
  );
};

/* ── Main component ──────────────────────────────────────────────── */
const NewEventDialog = ({
  isOpen,
  onClose,
  newEventData,
  setNewEventData,
  handleSaveNewEvent,
  spanishMonths,
  getYears,
  getDaysInMonth,
  timeZone,
  existingEvents = [],
  espaciosActivos = [],
  espaciosPorId = {},
  permiteEventosSimultaneos = true,
}) => {
  const prevEventDataRef = useRef({});
  const [showAdvancedHorarios, setShowAdvancedHorarios] = useState(false);
  const [eligiendoCliente, setEligiendoCliente] = useState(false);
  const [showClashConfirm, setShowClashConfirm] = useState(false);
  // Cliente del directorio (FRA-354): elegirlo llena los campos del evento
  // desde su ficha y lo liga. Es obligatorio para un evento (no para un
  // bloqueo): un evento sin cliente no se guarda.
  const { clientes } = useVenue();
  const clienteElegido =
    newEventData.clienteId
      ? (clientes || []).find((c) => c.id === newEventData.clienteId) || null
      : null;
  const elegirCliente = (c) => {
    if (!c) {
      setNewEventData({ ...newEventData, clienteId: null });
      return;
    }
    const campos = camposEventoDesdeCliente(c);
    // Scouting y reunión guardan al contacto en `contacto`; se espeja para
    // que sus pantallas (que leen ese campo) sigan mostrándolo.
    const extra = newEventData.eventType === "event" ? {} : { contacto: nombreCliente(c) };
    setNewEventData({ ...newEventData, ...campos, ...extra });
  };

  const clashingEvents = getClashingEvents({
    start: newEventData.startTime,
    end: newEventData.endTime,
    espaciosIds: newEventData.espaciosIds,
    existingEvents,
    espaciosPorId,
    permiteEventosSimultaneos,
  });

  const validateFields = () => {
    if (!newEventData.eventType) {
      showAlert("Selecciona un tipo de evento", "error");
      return false;
    }
    if (!newEventData.nombre || !newEventData.nombre.trim()) {
      showAlert("El nombre es obligatorio", "error");
      return false;
    }
    if (["event", "scouting"].includes(newEventData.eventType) && !newEventData.clienteId) {
      showAlert(newEventData.eventType === "event" ? "Elige el cliente del evento" : "Elige al prospecto del scouting", "error");
      return false;
    }
    const start = DateTime.fromISO(newEventData.startTime);
    const end = DateTime.fromISO(newEventData.endTime);
    if (end <= start) {
      showAlert("La fecha de fin debe ser posterior a la de inicio", "error");
      return false;
    }
    if (newEventData.eventType === "event") {
      const ordered = [
        { key: "startTime", label: "Inicio del Bloqueo" },
        { key: "inicioMontaje", label: "Inicio del Montaje" },
        { key: "inicioEvento", label: "Inicio del Evento" },
        { key: "finEvento", label: "Fin del Evento" },
        { key: "finMontaje", label: "Fin del Desmontaje" },
        { key: "endTime", label: "Fin del Bloqueo" },
      ];
      for (let i = 1; i < ordered.length; i++) {
        const prev = DateTime.fromISO(newEventData[ordered[i - 1].key]);
        const curr = DateTime.fromISO(newEventData[ordered[i].key]);
        if (curr.isValid && prev.isValid && curr < prev) {
          showAlert(
            `"${ordered[i].label}" no puede ser antes que "${ordered[i - 1].label}"`,
            "error"
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateFields()) return;
    if (clashingEvents.length > 0) {
      setShowClashConfirm(true);
      return;
    }
    handleSaveNewEvent({
      ...newEventData,
      precioAcordado: newEventData.precioAcordado
        ? Number(newEventData.precioAcordado)
        : 0,
      clientType: newEventData.clientType || "fisica",
    });
  };

  const handleConfirmSaveClash = async () => {
    setShowClashConfirm(false);
    handleSaveNewEvent({
      ...newEventData,
      precioAcordado: newEventData.precioAcordado
        ? Number(newEventData.precioAcordado)
        : 0,
      clientType: newEventData.clientType || "fisica",
    });
  };

  const handleCancelSaveClash = () => {
    setShowClashConfirm(false);
  };

  useEffect(() => {
    if (isOpen) {
      setShowAdvancedHorarios(false);
      const startDefault = getDefaultStart();
      const endDefault = getDefaultEnd();
      let updates = {};
      if (newEventData.eventType === "event") {
        if (!newEventData.startTime) updates.startTime = startDefault;
        if (!newEventData.inicioMontaje) updates.inicioMontaje = startDefault;
        if (!newEventData.inicioEvento) updates.inicioEvento = startDefault;
        if (!newEventData.finEvento) updates.finEvento = endDefault;
        if (!newEventData.finMontaje) updates.finMontaje = endDefault;
        if (!newEventData.endTime) updates.endTime = endDefault;
      } else {
        if (!newEventData.startTime) updates.startTime = startDefault;
        if (!newEventData.endTime) updates.endTime = endDefault;
      }
      if (Object.keys(updates).length > 0) {
        setNewEventData({ ...newEventData, ...updates });
      }
    }
    // eslint-disable-next-line
  }, [isOpen, newEventData.eventType]);

  useEffect(() => {
    if (isOpen) {
      if (
        newEventData.eventType !== "event" &&
        newEventData.endTime < newEventData.startTime
      ) {
        const end = DateTime.fromISO(newEventData.startTime)
          .plus({ minutes: 45 })
          .toISO();
        setNewEventData({ ...newEventData, endTime: end });
      }
    }
    // eslint-disable-next-line
  }, [isOpen, newEventData.startTime]);

  // Ordered keys for the 6 event dates
  const orderedKeys = [
    "startTime",
    "inicioMontaje",
    "inicioEvento",
    "finEvento",
    "finMontaje",
    "endTime",
  ];

  useEffect(() => {
    if (!isOpen || newEventData.eventType !== "event") {
      prevEventDataRef.current = {};
      return;
    }

    // Detect which field changed
    let changedKey = null;
    for (const key of orderedKeys) {
      if (
        prevEventDataRef.current[key] !== newEventData[key] &&
        newEventData[key]
      ) {
        changedKey = key;
        break;
      }
    }

    if (!changedKey) {
      prevEventDataRef.current = { ...newEventData };
      return;
    }

    const changedIdx = orderedKeys.indexOf(changedKey);
    const changedDT = DateTime.fromISO(newEventData[changedKey]);
    if (!changedDT.isValid) {
      prevEventDataRef.current = { ...newEventData };
      return;
    }

    const updates = {};

    if (!showAdvancedHorarios && (changedKey === "startTime" || changedKey === "endTime")) {
      // Simple mode: cluster the 4 hidden dates right after startTime
      const start = changedKey === "startTime"
        ? changedDT
        : DateTime.fromISO(newEventData.startTime);

      if (changedKey === "startTime") {
        updates.inicioMontaje = start.plus({ minutes: 5 }).toISO();
        updates.inicioEvento = start.plus({ minutes: 10 }).toISO();
        updates.finEvento = start.plus({ minutes: 15 }).toISO();
        updates.finMontaje = start.plus({ minutes: 20 }).toISO();
        // Push endTime forward if it fell behind
        if (
          newEventData.endTime &&
          DateTime.fromISO(newEventData.endTime) <= start
        ) {
          updates.endTime = start.plus({ hours: 3 }).toISO();
        }
      } else {
        // endTime changed — revert if it's before startTime and warn
        const startDT = DateTime.fromISO(newEventData.startTime);
        if (changedDT <= startDT) {
          updates.endTime = prevEventDataRef.current.endTime || startDT.plus({ hours: 3 }).toISO();
          showAlert("El fin del bloqueo no puede ser antes del inicio", "warning");
        }
      }
    } else {
      // Advanced mode: enforce ordering in both directions from the changed field.
      // Push all LATER fields forward so they're not before the changed value
      let floor = changedDT;
      for (let i = changedIdx + 1; i < orderedKeys.length; i++) {
        const key = orderedKeys[i];
        const val = newEventData[key] ? DateTime.fromISO(newEventData[key]) : null;
        if (!val || val < floor) {
          const pushed = floor.plus({ minutes: 5 });
          updates[key] = pushed.toISO();
          floor = pushed;
        } else {
          floor = val;
        }
      }

      // Pull all EARLIER fields back so they're not after the changed value
      let ceiling = changedDT;
      for (let i = changedIdx - 1; i >= 0; i--) {
        const key = orderedKeys[i];
        const val = newEventData[key] ? DateTime.fromISO(newEventData[key]) : null;
        if (!val || val > ceiling) {
          const pulled = ceiling.minus({ minutes: 5 });
          updates[key] = pulled.toISO();
          ceiling = pulled;
        } else {
          ceiling = val;
        }
      }
    }

    if (Object.keys(updates).length > 0) {
      setNewEventData((prev) => ({ ...prev, ...updates }));
    }
    prevEventDataRef.current = { ...newEventData, ...updates };
    // eslint-disable-next-line
  }, [
    isOpen,
    showAdvancedHorarios,
    newEventData.eventType,
    newEventData.startTime,
    newEventData.inicioMontaje,
    newEventData.inicioEvento,
    newEventData.finEvento,
    newEventData.finMontaje,
    newEventData.endTime,
  ]);

  // Base day for a new event: the day the user clicked in the calendar, or today,
  // in the venue timezone. New events default to spanning the FULL day (00:00–23:59)
  // rather than the current clock time — owners expect a fresh bloqueo/evento to
  // default to the whole day (Inicio 00:00, Fin 23:59).
  const getBaseDay = () => {
    const base =
      newEventData.clickInfo && newEventData.clickInfo.date
        ? DateTime.fromJSDate(newEventData.clickInfo.date).setZone(timeZone)
        : DateTime.now().setZone(timeZone);
    return base.startOf("day");
  };
  const getDefaultStart = () => getBaseDay().toISO();
  const getDefaultEnd = () =>
    getBaseDay().set({ hour: 23, minute: 59, second: 0, millisecond: 0 }).toISO();

  const config = EVENT_TYPE_CONFIG[newEventData.eventType];

  /* ── Shared date-time props ──────────────────────────────────── */
  const dtProps = { spanishMonths, getYears, getDaysInMonth, timeZone };

  /* ── Per-type form bodies ────────────────────────────────────── */
  // Elegir tipo reinicia los campos propios del tipo anterior y conserva lo
  // transversal (fechas, zona, espacios). Misma lógica que tenía el select.
  const elegirTipo = (value) => {
    if (!value) {
      setNewEventData({ eventType: null });
      return;
    }
    const baseData = {
      eventType: value,
      startTime: newEventData.startTime,
      endTime: newEventData.endTime,
      timeZone: newEventData.timeZone,
      clickInfo: newEventData.clickInfo,
    };
    if (newEventData.espaciosIds?.length) {
      baseData.espaciosIds = newEventData.espaciosIds;
    }
    if (value === "scouting") {
      setNewEventData({ ...baseData, reserved: false });
    } else if (value === "event") {
      setNewEventData({ ...baseData, totalPagado: 0 });
    } else {
      setNewEventData(baseData);
    }
  };

  // Cliente/contacto del directorio, el mismo bloque para evento, scouting
  // y reunión: nunca se captura suelto. Obligatorio en evento y scouting (es
  // un prospecto o un cliente); en reunión es opcional.
  const renderCliente = () => (
    <>
            {/* Cliente: siempre del directorio. Se elige o se da de alta con
                el mismo diálogo que usan leads y eventos; no hay captura
                suelta, que era lo que fabricaba clientes huérfanos. */}
            <Section icon={UserIcon} title={newEventData.eventType === "event" ? "Cliente" : "Contacto"}>
              {clienteElegido ? (
                <div className="flex items-center gap-3 rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800 px-4 py-3 shadow-sm">
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                    style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
                    aria-hidden="true"
                  >
                    {(nombreCliente(clienteElegido) || "?").trim().charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-zinc-900 dark:text-white truncate">
                        {nombreCliente(clienteElegido)}
                      </span>
                      {clienteElegido.clientType === "moral" && <Badge color="zinc">Empresa</Badge>}
                    </div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">
                      {[contactoCliente(clienteElegido), clienteElegido.correo, clienteElegido.telefono]
                        .filter(Boolean)
                        .join(" · ") || "Sin datos de contacto"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEligiendoCliente(true)}
                    className="shrink-0 rounded-lg border border-zinc-200 dark:border-zinc-600 px-2.5 py-1.5 text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setEligiendoCliente(true)}
                  className="group flex w-full items-center gap-3 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-4 py-3 text-left transition hover:border-[#1b4d2e] hover:bg-[#1b4d2e]/[0.04]"
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-dashed border-zinc-300 dark:border-zinc-600 text-zinc-400 transition group-hover:border-[#1b4d2e] group-hover:text-[#1b4d2e]"
                    aria-hidden="true"
                  >
                    <UserIcon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-zinc-900 dark:text-white">
                      Elegir cliente
                    </span>
                    <span className="block text-xs text-zinc-500 dark:text-zinc-400">
                      Del directorio, o dalo de alta aquí mismo
                    </span>
                  </span>
                  <span
                    className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold text-white shadow-sm"
                    style={{ backgroundColor: ACCENT }}
                  >
                    Buscar
                  </span>
                </button>
              )}
              <ClienteEventoDialog
                open={eligiendoCliente}
                datosDelEvento={{}}
                titulo={
                  newEventData.eventType === "event"
                    ? "Cliente del evento"
                    : newEventData.eventType === "scouting"
                      ? "Prospecto del scouting"
                      : "Contacto de la reunión"
                }
                subtitulo="Elígelo del directorio o dalo de alta"
                textoCrear="Crear cliente y continuar"
                origenCreacion="evento"
                zIndex={60}
                onClose={() => setEligiendoCliente(false)}
                onLigar={(clienteId, { cliente } = {}) => {
                  elegirCliente(cliente || { id: clienteId });
                  setEligiendoCliente(false);
                }}
              />
            </Section>
    </>
  );

  const renderEventTypeFields = () => {
    switch (newEventData.eventType) {
      case "bloqueo":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            <div className="flex flex-col gap-4">
            <Section icon={CalendarDaysIcon} title="Información">
              <DialogInput
                label="Razón del Bloqueo"
                type="text"
                value={newEventData.nombre || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, nombre: e.target.value })
                }
              />
              </Section>
            <Section icon={DocumentTextIcon} title="Notas" className="flex-1">
              <DialogTextarea
                value={newEventData.notes || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, notes: e.target.value })
                }
                rows={3}
              />
              </Section>
            </div>
            <Section icon={ClockIcon} title="Horarios">
              <DateTimeSelect
                label="Inicio"
                value={newEventData.startTime}
                onChange={(v) =>
                  setNewEventData({ ...newEventData, startTime: v })
                }
                {...dtProps}
              />
              <DateTimeSelect
                label="Fin"
                value={newEventData.endTime}
                onChange={(v) =>
                  setNewEventData({ ...newEventData, endTime: v })
                }
                {...dtProps}
              />
              {clashingEvents.length > 0 && (
                <ClashWarning events={clashingEvents} />
              )}
              </Section>
          </div>
        );

      case "event":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            <Section icon={SparklesIcon} title="Información del evento">
              <DialogInput
                label="Nombre del Evento"
                type="text"
                value={newEventData.nombre || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, nombre: e.target.value })
                }
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                <DialogInput
                  label="# de personas"
                  type="number"
                  value={newEventData.numPeople || ""}
                  onChange={(e) =>
                    setNewEventData({
                      ...newEventData,
                      numPeople: e.target.value,
                    })
                  }
                />
                <DialogInput
                  label="Vendedor"
                  type="text"
                  value={newEventData.vendedor || ""}
                  onChange={(e) =>
                    setNewEventData({
                      ...newEventData,
                      vendedor: e.target.value,
                    })
                  }
                />
              </div>
              </Section>
            {renderCliente()}
            <Section icon={ClockIcon} title="Horarios">
              <label className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 mb-1">
                <input
                  type="checkbox"
                  checked={showAdvancedHorarios}
                  onChange={(e) => setShowAdvancedHorarios(e.target.checked)}
                  className="rounded border-zinc-300 dark:border-zinc-600"
                  style={{ accentColor: ACCENT }}
                />
                Editar horarios avanzados
              </label>
              {showAdvancedHorarios ? (
                <>
                  <DateTimeSelect
                    label="Inicio del Bloqueo"
                    value={newEventData.startTime}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, startTime: v })
                    }
                    {...dtProps}
                  />
                  <DateTimeSelect
                    label="Inicio del Montaje"
                    value={newEventData.inicioMontaje}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, inicioMontaje: v })
                    }
                    {...dtProps}
                  />
                  <DateTimeSelect
                    label="Inicio del Evento"
                    value={newEventData.inicioEvento}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, inicioEvento: v })
                    }
                    {...dtProps}
                  />
                  <DateTimeSelect
                    label="Fin del Evento"
                    value={newEventData.finEvento}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, finEvento: v })
                    }
                    {...dtProps}
                  />
                  <DateTimeSelect
                    label="Fin del Desmontaje"
                    value={newEventData.finMontaje}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, finMontaje: v })
                    }
                    {...dtProps}
                  />
                  <DateTimeSelect
                    label="Fin del Bloqueo"
                    value={newEventData.endTime}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, endTime: v })
                    }
                    {...dtProps}
                  />
                </>
              ) : (
                <>
                  <DateTimeSelect
                    label="Inicio del Bloqueo"
                    value={newEventData.startTime}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, startTime: v })
                    }
                    {...dtProps}
                  />
                  <DateTimeSelect
                    label="Fin del Bloqueo"
                    value={newEventData.endTime}
                    onChange={(v) =>
                      setNewEventData({ ...newEventData, endTime: v })
                    }
                    {...dtProps}
                  />
                </>
              )}
              {clashingEvents.length > 0 && (
                <ClashWarning events={clashingEvents} />
              )}
              </Section>
            <div className="flex flex-col gap-4">
            <Section icon={CurrencyDollarIcon} title="Precio">
              <DialogInput
                label="Precio Acordado (MXN) (Opcional)"
                type="number"
                value={newEventData.precioAcordado || ""}
                onChange={(e) =>
                  setNewEventData({
                    ...newEventData,
                    precioAcordado: e.target.value,
                  })
                }
              />
              </Section>
            <Section icon={DocumentTextIcon} title="Notas" className="flex-1">
              <DialogTextarea
                value={newEventData.notes || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, notes: e.target.value })
                }
                rows={3}
              />
              </Section>
            </div>
          </div>
        );

      case "reunion":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            <Section icon={CalendarDaysIcon} title="Información">
              <DialogInput
                label="Nombre de Reunión"
                type="text"
                value={newEventData.nombre || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, nombre: e.target.value })
                }
              />
              </Section>
            {renderCliente()}
            <Section icon={ClockIcon} title="Horarios">
              <DateTimeSelect
                label="Inicio"
                value={newEventData.startTime}
                onChange={(v) =>
                  setNewEventData({ ...newEventData, startTime: v })
                }
                {...dtProps}
              />
              <DateTimeSelect
                label="Fin"
                value={newEventData.endTime}
                onChange={(v) =>
                  setNewEventData({ ...newEventData, endTime: v })
                }
                {...dtProps}
              />
              {clashingEvents.length > 0 && (
                <ClashWarning events={clashingEvents} />
              )}
              </Section>
            <Section icon={DocumentTextIcon} title="Notas" className="flex-1">
              <DialogTextarea
                value={newEventData.notes || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, notes: e.target.value })
                }
                rows={3}
              />
              </Section>
          </div>
        );

      case "scouting":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            <Section icon={CalendarDaysIcon} title="Información">
              <DialogInput
                label="Nombre del Scouting"
                type="text"
                value={newEventData.nombre || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, nombre: e.target.value })
                }
              />
              </Section>
            {renderCliente()}
            <Section icon={ClockIcon} title="Horarios">
              <DateTimeSelect
                label="Inicio"
                value={newEventData.startTime}
                onChange={(v) =>
                  setNewEventData({ ...newEventData, startTime: v })
                }
                {...dtProps}
              />
              <DateTimeSelect
                label="Fin"
                value={newEventData.endTime}
                onChange={(v) =>
                  setNewEventData({ ...newEventData, endTime: v })
                }
                {...dtProps}
              />
              {clashingEvents.length > 0 && (
                <ClashWarning events={clashingEvents} />
              )}
              </Section>
            <Section icon={DocumentTextIcon} title="Notas" className="flex-1">
              <DialogTextarea
                value={newEventData.notes || ""}
                onChange={(e) =>
                  setNewEventData({ ...newEventData, notes: e.target.value })
                }
                rows={3}
              />
              </Section>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} size="3xl">
        {/* Header */}
        <div
          className="rounded-t-[calc(1.5rem-1px)] sm:rounded-t-[calc(1rem-1px)] px-[--gutter] pt-4 pb-3.5 -mx-[--gutter] -mt-[--gutter]"
          style={{ backgroundColor: ACCENT + "0d" }}
        >
          {/* Una sola línea: icono, título y la etiqueta del tipo. El
              encabezado era dos renglones con aire de sobra. */}
          <div className="flex items-center gap-3 pr-6">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
              style={{ backgroundColor: ACCENT + "1a" }}
            >
              <CalendarDaysIcon className="h-4 w-4" style={{ color: ACCENT }} />
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
                {nameRender[newEventData.eventType]}
              </h2>
              {config && <Badge color={config.badge}>{config.label}</Badge>}
            </div>
          </div>
        </div>

        <DialogBody className="!mt-4">
          <div className="space-y-4">
            {/* Tipo y Espacios en una sola fila; el resto, en dos columnas
                por tipo. El formulario era una columna de ocho secciones. */}
            <div
              className={`grid grid-cols-1 gap-4 items-start ${
                newEventData.eventType && espaciosActivos.length > 0 ? "md:grid-cols-2" : ""
              }`}
            >
            {/* Type selector — always visible */}
            <Section icon={CalendarDaysIcon} title="Tipo">
              <div
                className={`grid gap-2 ${
                  newEventData.eventType && espaciosActivos.length > 0
                    ? "grid-cols-2"
                    : "grid-cols-2 sm:grid-cols-4"
                }`}
                role="radiogroup"
                aria-label="Tipo de evento"
              >
                {TIPOS_EVENTO.map((t) => {
                  const activo = newEventData.eventType === t.value;
                  const Icon = t.icon;
                  return (
                    <button
                      key={t.value}
                      type="button"
                      role="radio"
                      aria-checked={activo}
                      onClick={() => elegirTipo(t.value)}
                      className={`flex flex-col items-start gap-1.5 rounded-xl border px-3 py-2.5 text-left transition ${
                        activo
                          ? "border-transparent ring-2 shadow-sm bg-white dark:bg-zinc-800"
                          : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 hover:border-zinc-300 dark:hover:border-zinc-500"
                      }`}
                      style={activo ? { "--tw-ring-color": t.color } : undefined}
                    >
                      <span
                        className="flex h-7 w-7 items-center justify-center rounded-lg"
                        style={{ backgroundColor: t.color + "1a", color: t.color }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="text-sm font-semibold text-zinc-900 dark:text-white">{t.label}</span>
                      <span className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400">{t.hint}</span>
                    </button>
                  );
                })}
              </div>
            </Section>

            {/* Espacios — solo si el venue los maneja. Sin selección, el
                evento bloquea todo el venue (el default seguro). */}
            {newEventData.eventType && espaciosActivos.length > 0 && (
              <Section icon={Squares2X2Icon} title="Espacios">
                <EspaciosPicker
                  opciones={espaciosActivos}
                  value={newEventData.espaciosIds || []}
                  onChange={(ids) =>
                    setNewEventData({ ...newEventData, espaciosIds: ids })
                  }
                />
                <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                  {(newEventData.espaciosIds || []).length === 0
                    ? "Sin espacios seleccionados, este evento bloquea todo el venue."
                    : "El evento bloquea los espacios seleccionados (y los espacios que los contienen o son incompatibles con ellos)."}
                </p>
              </Section>
            )}

            </div>

            {newEventData.eventType && renderEventTypeFields()}
          </div>
        </DialogBody>

        {/* Sin tipo elegido no hay nada que guardar: la X del diálogo cierra.
            Las acciones aparecen con el formulario, no antes. */}
        {newEventData.eventType && (
        <DialogActions className="!mt-5">
          <Button onClick={onClose} outline>
            Cancelar
          </Button>
          <button
            onClick={handleSave}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition"
            style={{ backgroundColor: ACCENT }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#164026")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = ACCENT)
            }
          >
            Guardar
          </button>
        </DialogActions>
        )}
      </Dialog>

      {/* Clash confirmation */}
      <Alert open={showClashConfirm} onClose={handleCancelSaveClash}>
        <AlertTitle>Cruce de horarios</AlertTitle>
        <AlertDescription>
          El evento se cruza con otro evento o bloqueo existente. ¿Deseas
          guardar de todos modos?
        </AlertDescription>
        <AlertActions>
          <Button outline onClick={handleCancelSaveClash}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleConfirmSaveClash}>
            Guardar de todos modos
          </Button>
        </AlertActions>
      </Alert>
    </>
  );
};

export default NewEventDialog;
